import React, { useMemo, useEffect, useState } from "react";
import {
  TextField,
  Typography,
  Grid,
  Breadcrumbs,
  Link,
  Box,
  Button,
  Snackbar,
  Alert,
  Tooltip,
} from "@mui/material";
import Layout from "../../layout/Layout";
import { MaterialReactTable } from "material-react-table";
import { LoadingButton } from "@mui/lab";
import {
  addvendorAPI,
  fetchStatusByParent,
  fetchVendorAPI,
  listvendorAPI,
} from "../../config";
import { apiData } from "../../services/api_service";
import EditIcon from "@mui/icons-material/Edit";
import IconButton from "@mui/material/IconButton";
import { useNavigate } from "react-router-dom";
import AddIcon from "@mui/icons-material/Add";
import { DatePicker } from "@mui/x-date-pickers";
import ArrowBackIosIcon from "@mui/icons-material/ArrowBackIos";
import { emirates, vendorType } from "../../components/constants";
import moment from "moment";
import { FileDownload } from "@mui/icons-material";
import * as XLSX from "xlsx";
import { fitColumns } from "../../utils/excelFit";
import dayjs from "dayjs";
import { LocalizationProvider, MobileDatePicker } from "@mui/x-date-pickers";
import { AdapterDayjs } from "@mui/x-date-pickers/AdapterDayjs";
import { writeXlsxFrozen } from "../../utils/xlsxFreeze";

const Vendor1List = () => {
  const [columnFilters, setColumnFilters] = useState([]);
  const [sorting, setSorting] = useState([]);
  const [loading, setLoading] = useState(false);
  const [rowCount, setRowCount] = useState(0);
  const [pagination, setPagination] = useState({
    pageIndex: 0,
    pageSize: 100,
  });

  // ✅ Table Columns
  const [vendorData, setVendorsData] = useState([]);
  const [status, setStatus] = useState([]);
  const [vendorTypes, setvendorTypes] = useState([]);
  const [successMessage, setSuccessMessage] = useState("");
  const navigate = useNavigate();

  useEffect(() => {
    const fetchStatus = async () => {
      const data = {
        intParentId: 222,
      };

      try {
        const result = await apiData("get", fetchStatusByParent, data);
        if (result.success) {
          setvendorTypes(result.data);
        }
      } catch (error) {
        console.error("Failed to fetch units:", error);
      }
    };

    fetchStatus();
  }, []);

  useEffect(() => {
    const fetchStatus = async () => {
      const data = {
        intParentId: 1,
      };

      try {
        const result = await apiData("get", fetchStatusByParent, data);
        if (result.success) {
          setStatus(result.data);
        }
      } catch (error) {
        console.error("Failed to fetch units:", error);
      }
    };

    fetchStatus();
  }, []);

  useEffect(() => {
    async function fetchData() {
      const payload = {
        pagination: JSON.stringify({
          pageIndex: pagination.pageIndex,
          pageSize: pagination.pageSize,
        }),
        sorting: sorting.length > 0 ? JSON.stringify(sorting[0]) : null,
        search: columnFilters.length > 0 ? JSON.stringify(columnFilters) : null,
      };

      try {
        setLoading(true);
        const response = await apiData("get", fetchVendorAPI, payload);
        if (response.success) {
          const { result, totalCount } = response.data;
          setVendorsData(result);
          setRowCount(totalCount);
        }
      } catch (error) {
        console.error("Error fetching vendors:", error);
      } finally {
        setLoading(false);
      }
    }
    fetchData();
  }, [columnFilters, sorting, pagination]);

  const handleExportData = async () => {
    try {
      setLoading(true);

      // Prepare payload with all filters, sorting, search, but unlimited pageSize
      const payload = {
        pagination: JSON.stringify({
          pageIndex: 0,
          pageSize: 10000000,
        }),
        sorting: sorting.length > 0 ? JSON.stringify(sorting[0]) : null,
        search: columnFilters.length > 0 ? JSON.stringify(columnFilters) : null,
      };

      const response = await apiData("get", fetchVendorAPI, payload);
      const dataArray = Array.isArray(response.data.result)
        ? response.data.result
        : [];
      if (dataArray.length === 0) {
        alert("No data to export!");
        return;
      }

      const exportData = dataArray.map((item, index) => {
        const emirateName =
          emirates.find((e) => e.id == item.emirate)?.name ||
          item.emirate ||
          "";

        return {
          "#": index + 1,
          "Vendor Name": item.name || "",
          "Company Name": item.company_name || "",
          "PO BOX": item.po_box || "",
          "VAT No": item.vat_no || "",
          Phone: item.phone_number || "",
          Email: item.email || "",
          Emirate: emirateName,
          "Created At": item.created_at
            ? moment(item.created_at).format("DD-MM-YYYY")
            : "",
          Status:
            item.status === 2
              ? "Active"
              : item.status === 3
              ? "InActive"
              : "",
        };
      });

      const ws = XLSX.utils.json_to_sheet(exportData);
      const wb = XLSX.utils.book_new();
      fitColumns(ws);
      XLSX.utils.book_append_sheet(wb, ws, "vendor_list");

      const fileName = `vendor_list_${moment().format(
        "DD_MM_YYYY"
      )}.xlsx`;
      writeXlsxFrozen(XLSX, wb, fileName);
      setSuccessMessage("Excel Downloaded Successfully!");
    } catch (error) {
      console.error("Export failed:", error);
      alert("Failed to export data.");
    } finally {
      setLoading(false);
    }
  };

  const columns = useMemo(() => [
    {
      accessorKey: "name",
      header: "Supplier Name",
      Cell: ({ row }) => <strong>{row.original.name}</strong>, // bold text
      size: 150,
    },
    {
      accessorKey: "company_name",
      header: "Company Name",
      Cell: ({ row }) => row.original.company_name || "NA",
      size: 150,
    },
    {
      accessorKey: "po_box",
      header: "PO BOX",
      Cell: ({ row }) => {
        return row.original.po_box;
      },
      size: 100,
    },
    {
      accessorKey: "vat_no",
      header: "VAT No",
      Cell: ({ row }) => row.original.vat_no,
      size: 100,
    },
    {
      accessorKey: "phone_number",
      header: "Phone",
      size: 120,
      Cell: ({ row }) => row.original.phone_number,
    },
    {
      accessorKey: "email",
      header: "Email",
      Cell: ({ row }) => row.original.email,
      size: 160,
    },
    {
      accessorKey: "emirate",
      header: "Emirate",
      Cell: ({ row }) => {
        const name = emirates.find(
          (item) => item.id == row.original.emirate
        )?.name;
        return name || row.original.emirate || "N/A";
      },
      size: 110,
      filterVariant: "select",
      filterSelectOptions: emirates.map((e) => ({ text: e.name, value: e.id })),
    },
    {
      accessorKey: "created_at",
      header: "Created At",
      size: 110,
      Cell: ({ cell }) => dayjs(cell.getValue()).format("DD-MM-YYYY"),
      Filter: ({ column }) => {
        const value = column.getFilterValue() || null;
        return (
          <LocalizationProvider dateAdapter={AdapterDayjs}>
            <MobileDatePicker
              value={value ? dayjs(value, "YYYY-MM-DD") : null}
              inputFormat="DD-MM-YYYY"
              onChange={(newValue) => {
                if (newValue) {
                  const formatted = dayjs(newValue).format("YYYY-MM-DD");
                  column.setFilterValue(formatted);
                } else {
                  column.setFilterValue(null);
                }
              }}
              clearable
              renderInput={(params) => (
                <TextField
                  {...params}
                  size="small"
                  variant="standard"
                  placeholder="Select Date"
                  sx={{
                    "& .MuiInputBase-input": {
                      color: "#fff !important",
                    },
                    "& .MuiInput-underline:before": {
                      borderBottomColor: "#fff !important",
                    },
                    "& .MuiInput-underline:hover:not(.Mui-disabled):before": {
                      borderBottomColor: "#fff !important",
                    },
                    "& .MuiInput-underline:after": {
                      borderBottomColor: "#fff !important",
                    },
                    "& .MuiInputBase-input::placeholder": {
                      color: "#fff !important",
                      opacity: 0.7,
                    },
                  }}
                  InputProps={{
                    ...params.InputProps,
                    endAdornment: (
                      <>
                        {params.InputProps.endAdornment}
                        {value && (
                          <IconButton
                            size="small"
                            onClick={() => column.setFilterValue(null)}
                            sx={{ color: "#fff" }}
                          >
                            ✕
                          </IconButton>
                        )}
                      </>
                    ),
                  }}
                />
              )}
            />
          </LocalizationProvider>
        );
      },
    },
    {
      accessorKey: "status",
      header: "Status",
      size: 90,
      Cell: ({ row }) => {
        const name = status.find(
          (option) => option.id === row.original.status
        )?.name;
        return (
          <span
            style={{
              color: row.original.status === 2 ? "green" : "red",
              fontWeight: 600,
            }}
          >
            {name || "N/A"}
          </span>
        );
      },
      filterVariant: "select",
      filterSelectOptions: [
        {
          label: "Active",
          title: "Active",
          value: 2,
        },
        {
          label: "InActive",
          title: "InActive",
          value: 3,
        },
      ],
    },
    {
      accessorKey: "edit",
      header: "Action",
      size: 70,
      Cell: ({ row }) => (
        <IconButton
          color="primary"
          onClick={() => navigate(`/vendor/add/${row.original.vendor_id}`)} // pass row data to edit function
        >
          <EditIcon />
        </IconButton>
      ),
      enableSorting: false,
      enableColumnFilter: false,
    },
  ]);

  return (
    <Layout>
      <Snackbar
        open={!!successMessage}
        autoHideDuration={3000} // hide after 3 seconds
        onClose={() => setSuccessMessage("")}
        anchorOrigin={{ vertical: "top", horizontal: "right" }}
      >
        <Alert
          onClose={() => setSuccessMessage("")}
          severity="success"
          sx={{
            width: "100%",
            backgroundColor: "#28a745",
            color: "#fff",
            fontWeight: 600,
          }}
        >
          {successMessage}
        </Alert>
      </Snackbar>
      <Grid className="employee-form bold-header">
        <Grid container spacing={3}>
          <Grid
            textAlign="center"
            item
            className="form-lable"
            xs={12}
            lg={6}
            md={6}
          >
            <Typography className="page_title" textAlign="left">
              Supplier List
            </Typography>
            <Breadcrumbs aria-label="breadcrumb">
              <Link underline="hover" color="inherit" href="/dashboard">
                Dashboard
              </Link>
              <Typography color="text.primary">Suppliers</Typography>
              <Typography color="text.primary">List</Typography>
            </Breadcrumbs>
          </Grid>
          <Grid
            textAlign="right"
            item
            className="form-lable"
            xs={12}
            lg={6}
            md={6}
          >
            <LoadingButton
              size="medium"
              variant="contained"
              color="success"
              className="button_first"
              href="/vendor/add"
              startIcon={<AddIcon />}
            >
              Add Supplier
            </LoadingButton>
            <LoadingButton
              size="medium"
              variant="contained"
              color="primary"
              onClick={() => navigate(-1)}
              startIcon={<ArrowBackIosIcon />}
            >
              Back
            </LoadingButton>
          </Grid>
        </Grid>

        {/* Table */}
        <Grid className="form_design">
          <MaterialReactTable
            enableGlobalFilter={false}
            enableGlobalFiltering={false}
            columns={columns}
            data={vendorData}
            layoutMode="grid"
            muiToolbarAlertBannerProps={{
              sx: {
                "& .MuiButtonBase-root": {
                  color: "red",
                  fontWeight: "500",
                  width: "100px",
                  "&:hover": {
                    background:
                      "linear-gradient(90deg, rgba(255, 153, 153, 0.8) 30%, rgba(255, 187, 187, 0.9) 100%)",
                  },
                },
              },
            }}
            getRowId={(row) => row.id}
            enableRowNumbers
            initialState={{ showColumnFilters: false }}
            rowNumberMode="original"
            enableFullScreenToggle={false}
            enableStickyHeader
            manualFiltering
            manualPagination
            manualSorting
            onColumnFiltersChange={setColumnFilters}
            onPaginationChange={setPagination}
            onSortingChange={setSorting}
            rowCount={rowCount}
            muiLinearProgressProps={{
              color: "inherit",
              sx: { height: "4px" },
            }}
            state={{
              columnFilters,
              isLoading: loading,
              pagination,
              // showAlertBanner: isError,
              // showProgressBars: isRefetching,
              sorting,
            }}
            muiTableContainerProps={{
              sx: {
                maxHeight: "calc(100vh - 240px)",
                minHeight: 450,
                overflowY: "auto",
              },
            }}
            muiTableBodyRowProps={({ row }) => ({
              sx: {
                backgroundColor: row.index % 2 === 0 ? "#fffff" : "#edf8fd",
              },
            })}
            muiTableHeadCellProps={{
              sx: {
                padding: "8px",
                border: "1px solid #ccc",
                color: "#fff !important",
                fontWeight: "bold",
                backgroundColor: "#1976d2",
                "& svg": {
                  color: "#fff !important",
                },
                "& .MuiTableSortLabel-root": {
                  color: "#fff !important",
                  "&.Mui-active": {
                    color: "#fff !important",
                    "& .MuiTableSortLabel-icon": {
                      color: "#fff !important",
                    },
                  },
                },
                "& .MuiTableSortLabel-icon": {
                  color: "#fff !important",
                },
                "& .MuiInputBase-input": {
                  color: "#fff !important",
                  textFillColor: "#fff !important",
                },
                "& .MuiInputBase-input::placeholder": {
                  color: "#fff !important",
                  opacity: 0.8,
                },
                "& .MuiInput-underline:before": {
                  borderBottomColor: "#fff !important",
                },
                "& .MuiInput-underline:hover:not(.Mui-disabled):before": {
                  borderBottomColor: "#fff !important",
                },
                "& .MuiInput-underline:after": {
                  borderBottomColor: "#fff !important",
                },
              },
            }}
            muiTableBodyCellProps={{
              sx: {
                sx: {
                  padding: "3px 8px",
                  border: "1px solid #ccc",
                },
              },
            }}
            renderTopToolbarCustomActions={({ table }) => (
              <>
                <Box
                  sx={{
                    display: "flex",
                    gap: "1rem",
                    p: "0.5rem",
                    justifyContent: "space-between",
                    flexGrow: 1,

                    flexWrap: "wrap",
                  }}
                >
                  <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                    {/* RIGHT SIDE BUTTON */}
                    <Tooltip title="Export data to excel sheet" arrow>
                      <Button
                        onClick={handleExportData}
                        style={{ background: "#1a73e829" }}
                        startIcon={<FileDownload />}
                        // startIcon={<IconTableExport />}
                      >
                        Export To Excel
                      </Button>
                    </Tooltip>
                  </Box>
                </Box>
              </>
            )}
          />
        </Grid>
      </Grid>
    </Layout>
  );
};

export default Vendor1List;
